import type { Column, Table } from '@tanstack/react-table';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useMemo } from 'react';

export type DateItem = {
    id: string;
    label: string;
    children?: DateItem[];
    date?: string;
    indent: number;
};

type UseGetDateFilterDataProps<TData, TValue> = {
    disabled: boolean;
    table: Table<TData>;
    column: Column<TData, TValue>;
};

export function useGetDateFilterData<TData, TValue>({
    disabled,
    table,
    column,
}: UseGetDateFilterDataProps<TData, TValue>) {
    return useMemo(() => {
        if (disabled) {
            return {
                items: [],
                hierarchy: [],
                allIds: [],
            };
        }

        // create days level
        function createDaysLevel(dates: Map<number, string>) {
            return Array.from(dates.values())
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                .map((date) => ({
                    id: `date-${date}`,
                    label: format(new Date(date), 'd', { locale: de }),
                    date,
                    indent: 2,
                }));
        }

        // create months level
        function createMonthsLevel(year: string, months: Record<number, Map<number, string>>) {
            return Object.entries(months)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([month, dates]) => ({
                    id: `month-${year}-${month}`,
                    label: format(new Date(Number(year), Number(month)), 'MMMM', { locale: de }),
                    indent: 1,
                    children: createDaysLevel(dates),
                }));
        }

        // create years level and combine everything
        const hierarchicalItems: DateItem[] = Object.entries(getGroupedData(table.options.data, column))
            .sort(([a], [b]) => Number(b) - Number(a))
            .map(([year, months]) => ({
                id: `year-${year}`,
                label: `${year}`,
                indent: 0,
                children: createMonthsLevel(year, months),
            }));

        const flattenedItems = [
            {
                id: 'select-all',
                label: '(Alle auswählen)',
                indent: 0,
            },
            ...flattenData(hierarchicalItems),
            {
                id: 'select-blanks',
                label: '(Leere auswählen)',
                indent: 0,
            },
        ];

        return {
            items: flattenedItems,
            hierarchy: hierarchicalItems,
            allIds: flattenedItems.map((item) => item.id),
        };
    }, [column, disabled, table.options.data]);
}

function getGroupedData<TData, TValue>(tableData: TData[], column: Column<TData, TValue>) {
    const data = tableData.map((row) => {
        return (row as Record<string, string | null | undefined>)[column.id];
    });

    const groupedData = data.reduce(
        (acc, value) => {
            if (value === null || value === undefined) {
                return acc;
            }

            const date = new Date(value);
            const year = date.getFullYear();
            const month = date.getMonth();
            const day = date.getDate();

            if (!acc[year]) {
                acc[year] = {};
            }

            if (!acc[year][month]) {
                acc[year][month] = new Map<number, string>();
            }

            const existingDate = acc[year][month].get(day);
            if (!existingDate || new Date(value).getTime() > new Date(existingDate).getTime()) {
                acc[year][month].set(day, value);
            }

            return acc;
        },
        {} as Record<number, Record<number, Map<number, string>>>
    );

    return groupedData;
}

function flattenData(hierarchicalItems: DateItem[]) {
    // flatten the hierarchical structure for the Listbox
    const flattenedItems: DateItem[] = [];

    const flatten = (items: DateItem[]) => {
        items.forEach((item) => {
            flattenedItems.push(item);

            if (item.children) {
                flatten(item.children);
            }
        });
    };

    flatten(hierarchicalItems);

    return flattenedItems;
}
